import React from "react";
import { Link } from "react-router-dom";
import { Clock, Star, Truck, Flame } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatGNF, imgUrl } from "@/utils/format";
import { fmtReopen } from "@/utils/reopen";
import TTSButton from "@/components/TTSButton";

export default function RestaurantCard({ restaurant }) {
  const { t, i18n } = useTranslation();
  const r = restaurant;
  const ttsText = [r?.name, r?.tagline].filter(Boolean).join(". ").trim();
  return (
    <Link
      to={`/restaurants/${r.restaurant_id}`}
      data-testid={`restaurant-card-${r.restaurant_id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-sand shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-elevated">
        {r.cover_image ? (
          <img src={imgUrl(r.cover_image)} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-[#FFF5F6] to-[#FFE1D6] text-brand-red font-display text-2xl">
            {r.name?.[0]?.toUpperCase() || "N"}
          </div>
        )}
        {r.logo_image && (
          <img
            src={imgUrl(r.logo_image)}
            alt={`${r.name} logo`}
            data-testid={`restaurant-logo-${r.restaurant_id}`}
            className="absolute bottom-3 left-3 w-12 h-12 rounded-full object-cover border-2 border-white shadow-lg bg-white"
          />
        )}
        {!r.open && (
          <div className="absolute inset-0 bg-black/50 grid place-items-center">
            <span className="bg-white text-ink font-display text-sm px-4 py-2 rounded-full text-center" data-testid={`restaurant-closed-${r.restaurant_id}`}>
              {r.next_opening_at
                ? t("card.closedUntil", { when: fmtReopen(r.next_opening_at, i18n.language) })
                : t("card.closed")}
            </span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="bg-white/95 backdrop-blur-sm text-xs font-semibold rounded-full px-3 py-1 flex items-center gap-1">
            <Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />
            {r.rating?.toFixed?.(1) || r.rating}
          </span>
          {r.open && r.busy && (
            <span data-testid={`restaurant-busy-${r.restaurant_id}`} className="bg-[#E67E22] text-white text-xs font-semibold rounded-full px-3 py-1 flex items-center gap-1">
              <Flame className="w-3 h-3" /> {t("card.busy")}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className="bg-white/95 backdrop-blur-sm text-xs font-semibold rounded-full px-3 py-1 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {r.delivery_time_min} min
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold text-ink truncate">{r.name}</h3>
            <p className="text-sm text-mute truncate">{r.tagline}</p>
          </div>
          <span className="text-xs uppercase tracking-widest text-brand-green shrink-0">{r.zone}</span>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-mute">
          <span className="flex items-center gap-1"><Truck className="w-3 h-3" /> {formatGNF(r.delivery_fee)}</span>
          <span>•</span>
          <span>{t("card.min")} {formatGNF(r.min_order)}</span>
        </div>
        {ttsText && (
          <div className="mt-3" onClick={(e) => e.preventDefault()}>
            <TTSButton
              text={ttsText}
              testId={`restaurant-tts-${r.restaurant_id}`}
              size="xs"
              ariaLabel={t("card.listenAbout", { name: r.name })}
            />
          </div>
        )}
      </div>
    </Link>
  );
}
